import { useEffect, useRef, useState } from "react";
import type { JourneyEventDTO } from "@/lib/api";
import {
  PLAYBACK_CARD_STAGGER_MS,
  PLAYBACK_SYNTHESISER_MS,
  consumePlayback,
  scalePhaseMs,
  usePlaybackStage,
  type StageKey,
} from "@/lib/playback";

export type PlaybackStatus = "idle" | "playing" | "complete";

export type PlaybackState = {
  status: PlaybackStatus;
  visibleEventIds: Set<string>;
  isPlaying: boolean;
  synthesiserActive: boolean;
  revealFindings: boolean;
  revealOutcome: boolean;
  cardDelayMs: (index: number) => number;
};

type Options = {
  stage: StageKey;
  appId: string;
  events: JourneyEventDTO[];
  enabled: boolean;
  cardCount: number;
};

export function usePlayback({ stage, appId, events, enabled, cardCount }: Options): PlaybackState {
  const { setPlayingStage } = usePlaybackStage();
  const decidedRef = useRef(false);
  const eventsRef = useRef<JourneyEventDTO[]>(events);
  const cardCountRef = useRef<number>(cardCount);
  const [status, setStatus] = useState<PlaybackStatus>("idle");
  const [visibleEventIds, setVisibleEventIds] = useState<Set<string>>(new Set());
  const [synthesiserActive, setSynthesiserActive] = useState(false);
  const [revealFindings, setRevealFindings] = useState(false);
  const [revealOutcome, setRevealOutcome] = useState(false);

  eventsRef.current = events;
  cardCountRef.current = cardCount;

  useEffect(() => {
    if (!enabled || decidedRef.current) return;
    decidedRef.current = true;

    const currentEvents = eventsRef.current;
    const currentCardCount = cardCountRef.current;
    const shouldPlay = consumePlayback(appId, stage);
    if (!shouldPlay || currentEvents.length === 0) {
      setStatus("complete");
      setVisibleEventIds(new Set(currentEvents.map((e) => e.id)));
      setSynthesiserActive(false);
      setRevealFindings(true);
      setRevealOutcome(true);
      return;
    }

    setStatus("playing");
    setVisibleEventIds(new Set());
    setSynthesiserActive(false);
    setRevealFindings(false);
    setRevealOutcome(false);
    setPlayingStage(stage);

    const timers: ReturnType<typeof setTimeout>[] = [];

    const byAgent = new Map<string, JourneyEventDTO[]>();
    const agentOrder: string[] = [];
    for (const e of currentEvents) {
      let arr = byAgent.get(e.agent_name);
      if (!arr) {
        arr = [];
        byAgent.set(e.agent_name, arr);
        agentOrder.push(e.agent_name);
      }
      arr.push(e);
    }

    let waveEnd = 0;
    for (const name of agentOrder) {
      const list = byAgent.get(name)!;
      let t = 0;
      for (const ev of list) {
        const d = scalePhaseMs(ev.duration_ms);
        t += d;
        const showAt = t;
        timers.push(
          setTimeout(() => {
            setVisibleEventIds((prev) => {
              const next = new Set(prev);
              next.add(ev.id);
              return next;
            });
          }, showAt),
        );
      }
      if (t > waveEnd) waveEnd = t;
    }

    let completeAt = waveEnd;

    if (stage === "analysis") {
      timers.push(setTimeout(() => setSynthesiserActive(true), waveEnd));
      const synthEnd = waveEnd + PLAYBACK_SYNTHESISER_MS;
      timers.push(
        setTimeout(() => {
          setSynthesiserActive(false);
          setRevealFindings(true);
        }, synthEnd),
      );
      const outcomeAt = synthEnd + Math.max(0, currentCardCount) * PLAYBACK_CARD_STAGGER_MS;
      timers.push(setTimeout(() => setRevealOutcome(true), outcomeAt));
      completeAt = outcomeAt;
    } else if (stage === "coordination") {
      timers.push(
        setTimeout(() => {
          setRevealFindings(true);
        }, waveEnd),
      );
      const outcomeAt = waveEnd + Math.max(0, currentCardCount) * PLAYBACK_CARD_STAGGER_MS;
      timers.push(setTimeout(() => setRevealOutcome(true), outcomeAt));
      completeAt = outcomeAt;
    } else {
      timers.push(
        setTimeout(() => {
          setRevealFindings(true);
          setRevealOutcome(true);
        }, waveEnd),
      );
      completeAt = waveEnd;
    }

    timers.push(
      setTimeout(() => {
        setStatus("complete");
        setPlayingStage(null);
      }, completeAt),
    );

    return () => {
      for (const t of timers) clearTimeout(t);
      setPlayingStage(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  return {
    status,
    visibleEventIds,
    isPlaying: status === "playing",
    synthesiserActive,
    revealFindings,
    revealOutcome,
    cardDelayMs: (index: number) => index * PLAYBACK_CARD_STAGGER_MS,
  };
}
